export interface LayoutContext {
  setPageName: (name: string) => void;
}

export interface IFeatureReport {
  featureTypeIds: string[]
}

export interface Attachment {
  id: string
  filename: string
  url: string
  thumbnailUrl: string
  uploadDate: string
  dateTaken: string | null
  createdByName: string
}

export enum GeometeryType {
  Polygon,
  Line,
  Point,
  Annulus
}

export interface Point {
  latitude: number
  longitude: number
}

export interface Site {
  id: string
  name: string
  description?: string
  tenantId: string
  zoom: number
  latitude: number,
  longitude: number,
  habitatCodeIds: string[],
}

export abstract class Feature {
  constructor(id: string | null, name: string, description: string | undefined | null, tenantId: string, siteId: string, ref: string | null, areaId: string | null, priorityCodeId: string, codeId: string | null, hpi: boolean, currentConditionCodeId: string, conditionSummary: string | null, plannedConditionCodeId: string | null, plannedDate: string | null, enhancementOpportunities: string | null, nativePlantSpecies: string | null, invasivePlantSpecies: string | null, animalsObserved: string | null, coordinates: Point[][], heading: number, zoom: number, centre: Point, type: GeometeryType, attachments: Attachment[]) {
    this.id = id
    this.name = name
    this.description = description
    this.tenantId = tenantId
    this.siteId = siteId
    this.ref = ref
    this.areaId = areaId
    this.priorityCodeId = priorityCodeId
    this.codeId = codeId
    this.hpi = hpi
    this.currentConditionCodeId = currentConditionCodeId
    this.conditionSummary = conditionSummary
    this.plannedConditionCodeId = plannedConditionCodeId
    this.plannedDate = plannedDate
    this.enhancementOpportunities = enhancementOpportunities
    this.nativePlantSpecies = nativePlantSpecies
    this.invasivePlantSpecies = invasivePlantSpecies
    this.animalsObserved = animalsObserved
    this.coordinates = coordinates
    this.heading = heading
    this.zoom = zoom
    this.centre = centre
    this.type = type
    this.attachments = attachments
  }

  id: string | null;
  name: string;
  description?: string | null | undefined;
  tenantId: string;
  siteId: string;
  ref: string | null;
  areaId: string | null;
  priorityCodeId: string;
  codeId: string | null;
  hpi: boolean
  currentConditionCodeId: string;
  conditionSummary: string | null;
  plannedConditionCodeId: string | null
  plannedDate: string | null
  enhancementOpportunities: string | null
  nativePlantSpecies: string | null
  invasivePlantSpecies: string | null
  animalsObserved: string | null
  coordinates: Point[][];
  heading: number;
  readonly type: GeometeryType;
  zoom: number;
  centre: Point;
  attachments: Attachment[]
}

export class PointPointOfInterest extends Feature {
  constructor(
      id: string | null,
      name: string,
      description: string | undefined | null,
      tenantId: string,
      siteId: string,
      ref: string | null,
      areaId: string | null,
      priorityCodeId: string, 
      codeId: string | null,
      hpi: boolean,
      conditionCodeId: string,
      conditionSummary: string | null,
      plannedConditionCodeId: string | null,
      plannedDate: string | null,
      enhancementOpportunities: string | null,
      nativePlantSpecies: string | null,
      invasivePlantSpecies: string | null,
      animalsObserved: string | null,
      cordinates: Point[][],
      heading: number,
      zoom: number,
      centre: Point,
      attachments:Attachment[]) {
    super(id, name, description, tenantId, siteId, ref, areaId, priorityCodeId, codeId, hpi, conditionCodeId, conditionSummary, plannedConditionCodeId, plannedDate, enhancementOpportunities, nativePlantSpecies, invasivePlantSpecies, animalsObserved, cordinates, heading, zoom, centre, GeometeryType.Point, attachments)
     
    }
}

export class PolylinePointOfInterest extends Feature {
  constructor(
      id: string | null,
      name: string,
      description: string | undefined | null,
      tenantId: string,
      siteId: string,
      ref: string | null,
      areaId: string | null,
      priorityCodeId: string,
      codeId: string | null,
      hpi: boolean,
      conditionCodeId: string,
      conditionSummary: string | null,
      plannedConditionCodeId: string | null,
      plannedDate: string | null,
      enhancementOpportunities: string | null,
      nativePlantSpecies: string | null,
      invasivePlantSpecies: string | null,
      animalsObserved: string | null,
      cordinates: Point[][],
      heading: number,
      zoom: number,
      centre: Point,
      attachments:Attachment[]) {
        super(id, name, description, tenantId, siteId, ref, areaId, priorityCodeId, codeId, hpi, conditionCodeId, conditionSummary, plannedConditionCodeId, plannedDate, enhancementOpportunities, nativePlantSpecies, invasivePlantSpecies, animalsObserved, cordinates, heading, zoom, centre, GeometeryType.Line, attachments)
    }

    length: number = -1
}

export class PolygonPointOfInterest extends Feature {
  constructor(
    id: string | null,
    name: string,
    description: string | undefined | null,
    tenantId: string,
    siteId: string,
    ref: string | null,
    areaId: string | null,
    priorityCodeId: string,
    codeId: string | null,
    hpi: boolean,
    conditionCodeId: string,
    conditionSummary: string | null,
    plannedConditionCodeId: string | null,
    plannedDate: string | null,
    enhancementOpportunities: string | null,
    nativePlantSpecies: string | null,
    invasivePlantSpecies: string | null,
    animalsObserved: string | null,
    cordinates: Point[][],
    heading: number,
    zoom: number,
    centre: Point,
    attachments:Attachment[]) {
        super(id, name, description, tenantId, siteId, ref, areaId, priorityCodeId, codeId, hpi, conditionCodeId, conditionSummary, plannedConditionCodeId, plannedDate, enhancementOpportunities, nativePlantSpecies, invasivePlantSpecies, animalsObserved, cordinates, heading, zoom, centre, GeometeryType.Polygon, attachments)
    }

    area: number = -1
    perimeter: number = -1
}

export class AnnulusPointOfInterest extends Feature {
  constructor(
    id: string | null,
    name: string,
    description: string | undefined | null,
    tenantId: string,
    siteId: string,
    ref: string | null,
    areaId: string | null,
    priorityCodeId: string,
    codeId: string | null,
    hpi: boolean,
    conditionCodeId: string,
    conditionSummary: string | null,
    plannedConditionCodeId: string | null,
    plannedDate: string | null,
    enhancementOpportunities: string | null,
    nativePlantSpecies: string | null,
    invasivePlantSpecies: string | null,
    animalsObserved: string | null,
    cordinates: Point[][],
    heading: number,
    zoom: number,
    centre: Point,
    attachments:Attachment[]) {
        super(id, name, description, tenantId, siteId, ref, areaId, priorityCodeId, codeId, hpi, conditionCodeId, conditionSummary, plannedConditionCodeId, plannedDate, enhancementOpportunities, nativePlantSpecies, invasivePlantSpecies, animalsObserved, cordinates, heading, zoom, centre, GeometeryType.Annulus, attachments)
    }

    area: number = -1
    perimeter: number = -1
}